/**
 * Announcements console (0509, founder 2026-10-25: "a post from Postopia
 * account goes to everyone's feed or Vibes"). Lists @postopia's recent posts
 * and Vibes; "Show to everyone" puts a post at a reserved row of everyone's
 * For You, or a Vibe first on everyone's Vibes rail, labelled "From Postopia",
 * for 7 days or until each viewer has opened it. One post and one Vibe at a
 * time: marking a new one clears the previous one of its kind. Posts can also
 * be marked from their own menu in the app; Vibes only here.
 */

import { parseServerDate } from '@postopia/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { AdminShell } from '../components/AdminShell';
import { PageShell } from '../components/ui/PageShell';
import {
  AnnouncementAdminError,
  announcementsApi,
  type AnnouncementRow,
} from '../lib/announcementsApi';

export const Route = createFileRoute('/announcements')({
  component: AnnouncementsRoute,
});

function AnnouncementsRoute() {
  return (
    <AdminShell mode="feature" featureName="Announcements">
      <AnnouncementsConsole />
    </AdminShell>
  );
}

const fmt = (iso: string) =>
  parseServerDate(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const ERRORS: Record<string, string> = {
  not_eligible: 'It can’t be shown: it must be public, top level and not expired (a Vibe must be a Short Vibe).',
  not_found: 'That post is not a @postopia post any more.',
};

function AnnouncementsConsole() {
  const [rows, setRows] = useState<AnnouncementRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setRows(await announcementsApi.list());
    } catch (e) {
      setRows([]);
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  async function toggle(r: AnnouncementRow) {
    setBusyId(r.id);
    setError(null);
    try {
      await announcementsApi.set(r.id, !r.announcement_until);
      await load();
    } catch (e) {
      setError(
        e instanceof AnnouncementAdminError ? (ERRORS[e.code] ?? e.message) : String(e),
      );
    } finally {
      setBusyId(null);
    }
  }

  const card: React.CSSProperties = {
    background: 'var(--c-bg-surface)',
    border: '1px solid var(--c-border-hairline)',
    borderRadius: 10,
    padding: 14,
    display: 'flex',
    gap: 14,
    alignItems: 'center',
  };

  return (
    <PageShell
      title="Announcements"
      description={
        <>
          Show a @postopia post or Vibe to everyone, labelled “From Postopia”: a post takes row 2 of
          For You, a Vibe goes first on the Vibes rail. It lasts 7 days, or until each person has
          opened it. One post and one Vibe at a time.
        </>
      }
    >
      {error ? (
        <p role="alert" style={{ fontSize: 14, color: '#9A2A12', margin: '0 0 12px' }}>
          {error}
        </p>
      ) : null}
      {rows === null ? (
        <p style={{ color: 'var(--c-fg-tertiary)', fontSize: 14 }}>Loading…</p>
      ) : rows.length === 0 ? (
        <p style={{ color: 'var(--c-fg-tertiary)', fontSize: 14 }}>@postopia has no posts yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rows.map((r) => {
            const on = !!r.announcement_until;
            return (
              <div key={r.id} style={card}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 0.3,
                        padding: '1px 8px',
                        borderRadius: 999,
                        background: 'var(--c-bg-surface-alt)',
                        color: 'var(--c-fg-secondary)',
                      }}
                    >
                      {r.is_vibe ? (r.vibe_kind === 'stream' ? 'Stream Vibe' : 'Vibe') : 'Post'}
                    </span>
                    {on ? (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: 0.3,
                          padding: '1px 8px',
                          borderRadius: 999,
                          background: '#BA533A',
                          color: '#FAF7F2',
                        }}
                      >
                        Shown to everyone until {fmt(r.announcement_until!)}
                      </span>
                    ) : null}
                    <span style={{ fontSize: 12, color: 'var(--c-fg-tertiary)' }}>
                      {fmt(r.created_at)}
                    </span>
                  </div>
                  <p
                    style={{
                      fontSize: 13,
                      color: 'var(--c-fg-default)',
                      margin: '6px 0 0',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {r.excerpt?.trim() || '(no text)'}
                  </p>
                  <p style={{ fontSize: 12, color: 'var(--c-fg-tertiary)', margin: '4px 0 0' }}>
                    {r.views_count} views · opened by {r.seen_by} signed-in people
                    {r.expires_at ? ` · expires ${fmt(r.expires_at)}` : ''}
                  </p>
                </div>
                <button
                  type="button"
                  disabled={busyId === r.id || (!on && !r.eligible)}
                  onClick={() => void toggle(r)}
                  title={!on && !r.eligible ? ERRORS.not_eligible : undefined}
                  style={{
                    padding: '8px 14px',
                    borderRadius: 8,
                    border: on ? '1px solid var(--c-border-strong)' : 0,
                    background: on ? 'transparent' : 'var(--c-fg-default)',
                    color: on ? 'var(--c-fg-default)' : 'var(--c-bg-page)',
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: busyId === r.id || (!on && !r.eligible) ? 'default' : 'pointer',
                    opacity: busyId === r.id || (!on && !r.eligible) ? 0.5 : 1,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {on ? 'Stop showing' : 'Show to everyone'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </PageShell>
  );
}
